import { useGetQSetByTaskId } from '@exam-room/graphql/preview';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  ExamEventContext as ExamEventPreviewContext,
  type ExamEvent,
  type ExamEventProviderProps,
} from '../exam-event';

import { convertQSetToMockExamData } from './utils';

const MOCK_EXAM_EVENT_DATA = {
  id: 'preview-event-id',
  title: 'Exam Room Preview',
  subTitle: null,
  type: null,
  metadata: {
    result: {
      display: true,
      delayedDays: 0,
      hasCertificate: true,
    },
    attachDocument: null,
    videoURL:
      'https://storage.googleapis.com/develop-set-asset-public/videos/Presentation%20CISA%20New%20Exam.webm',
  },
  coverImageUrl: null,
  exam: {
    title: 'Exam Room Preview',
    metadata: {
      randomType: 'RANDOM_BY_PART',
      examCourseCode: 'DOD',
    },
  },
  postponeDurationInMs: 0,
};

function ExamEventPreviewProvider({ children }: Readonly<ExamEventProviderProps>) {
  const { taskId } = useParams();
  const { data: qSetData, loading: isQsetLoading } = useGetQSetByTaskId({
    variables: {
      input: {
        taskId: taskId as string,
      },
    },
  });

  const mockExamsData = useMemo(() => convertQSetToMockExamData(qSetData), [qSetData]);

  const dynamicExamEvent = useMemo(() => {
    const now = new Date();
    let accumulatedSeconds = 0;
    const events = mockExamsData.erGetExamsByIds.map((exam, index) => {
      const startedAt = new Date(now.getTime() + accumulatedSeconds * 1000);
      const endedAt = new Date(startedAt.getTime() + exam.metadata.timeLimit * 1000);
      accumulatedSeconds += exam.metadata.timeLimit;
      return {
        order: index,
        examId: exam.id,
        endedAt: endedAt.toISOString(),
        startedAt: startedAt.toISOString(),
      };
    });

    return {
      ...MOCK_EXAM_EVENT_DATA,
      metadata: {
        ...MOCK_EXAM_EVENT_DATA.metadata,
        events,
      },
      startDate: now,
      endDate: new Date(now.getTime() + accumulatedSeconds * 1000),
      exam: {
        ...MOCK_EXAM_EVENT_DATA.exam,
        id: qSetData?.excGetQSetByTaskId?.qSet?.id ?? 'preview-exam-id',
        metadata: {
          ...MOCK_EXAM_EVENT_DATA.exam.metadata,
          qConfigId: qSetData?.excGetQSetByTaskId?.qSet?.qConfigId ?? '',
          timeLimit: accumulatedSeconds,
          examCourseId: qSetData?.excGetQSetByTaskId?.qSet?.examCourseId ?? '',
          passingCriteria: {
            total:
              qSetData?.excGetQSetByTaskId?.qSet?.qConfig?.metadata?.passingCriteria?.value ?? 0,
            byStructure:
              qSetData?.excGetQSetByTaskId?.qSet?.metadata?.passingCriteria?.byStructure ?? [],
          },
        },
      },
    } as ExamEvent;
  }, [mockExamsData, qSetData]);

  const { order, examId, startedAt, endedAt } = dynamicExamEvent.metadata.events[0];
  const closestExam = {
    order,
    examId,
    startedAt: new Date(startedAt),
    endedAt: new Date(endedAt),
  };

  const contextValue = {
    examEvent: dynamicExamEvent,
    loading: false,
    error: undefined,
    closestExam,
    examsData: mockExamsData.erGetExamsByIds,
    isLoadingExams: isQsetLoading,
    examDuration: {
      eventOrder: 0,
      extendedDurationInMs: 0,
      postponedDurationInMs: 0,
      totalManualExtendedDurationInMs: 0,
    },
    examDurationError: undefined,
  };

  return (
    <ExamEventPreviewContext.Provider value={contextValue}>
      {children}
    </ExamEventPreviewContext.Provider>
  );
}

export { ExamEventPreviewProvider };
